import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import * as ZXing from 'html5-qrcode/third_party/zxing-js.umd';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Camera, X, CircleDot } from 'lucide-react';
import { cn } from '../../lib/utils';

// ─── ZXing hints ──────────────────────────────────────────────────────────────
// POSSIBLE_FORMATS restricts ZXing to 1D formats only (faster, more reliable).
// TRY_HARDER enables multi-row scanning and handles slightly rotated barcodes.
// A fresh MultiFormatReader is created per decode call — reusing one across
// failed attempts carries stale exception state that can skip formats silently.
const ZXING_FORMATS = [
  ZXing.BarcodeFormat.CODE_128,
  ZXing.BarcodeFormat.CODE_39,
  ZXing.BarcodeFormat.EAN_13,
  ZXing.BarcodeFormat.EAN_8,
  ZXing.BarcodeFormat.UPC_A,
  ZXing.BarcodeFormat.UPC_E,
  ZXing.BarcodeFormat.ITF,
  ZXing.BarcodeFormat.CODABAR,
  ZXing.BarcodeFormat.QR_CODE,
  ZXing.BarcodeFormat.DATA_MATRIX,
];

function makeZXingReader() {
  const hints = new Map<ZXing.DecodeHintType, unknown>();
  hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
  hints.set(ZXing.DecodeHintType.TRY_HARDER, true);
  return new ZXing.MultiFormatReader(false, hints);
}

// ─── Image preprocessing helpers ──────────────────────────────────────────────
// Each helper returns a new canvas with the transformation applied.
// We resize to max 640px wide before decoding to keep ZXing fast on high-res
// camera feeds.

function resizedCanvas(source: HTMLVideoElement, maxWidth = 640): HTMLCanvasElement {
  const scale = Math.min(1, maxWidth / source.videoWidth);
  const w = Math.floor(source.videoWidth * scale);
  const h = Math.floor(source.videoHeight * scale);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d', { willReadFrequently: true })!.drawImage(source, 0, 0, w, h);
  return c;
}

type PixelTransform = (r: number, g: number, b: number) => [number, number, number];

function applyTransform(src: HTMLCanvasElement, fn: PixelTransform): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = src.width;
  out.height = src.height;
  const ctx = out.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(src, 0, 0);
  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const [nr, ng, nb] = fn(d[i], d[i + 1], d[i + 2]);
    d[i] = nr; d[i + 1] = ng; d[i + 2] = nb;
  }
  ctx.putImageData(imageData, 0, 0);
  return out;
}

// Standard grayscale — what ZXing normally does internally
const toGrayscale: PixelTransform = (r, g, b) => {
  const v = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  return [v, v, v];
};

// Red channel only → black-on-red becomes black-on-white (perfect contrast)
const redChannel: PixelTransform = (r) => [r, r, r];

// Green channel inverted → white-on-red becomes dark-on-light
// White(255,255,255)→G=255→inv=0 (dark bar), Red(255,0,0)→G=0→inv=255 (bright bg)
const greenChannelInverted: PixelTransform = (_, g) => {
  const v = 255 - g;
  return [v, v, v];
};

// Full inversion — helps any white-on-dark barcode
const invert: PixelTransform = (r, g, b) => [255 - r, 255 - g, 255 - b];

// Contrast boost — push luminance toward extremes
const highContrast: PixelTransform = (r, g, b) => {
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  const v = lum < 128
    ? Math.max(0, Math.round(lum * 0.4))
    : Math.min(255, Math.round(128 + (lum - 128) * 1.8));
  return [v, v, v];
};

const PREPROCESSING_PASSES: PixelTransform[] = [
  toGrayscale,       // pass 0: normal
  redChannel,        // pass 1: black-on-red → perfect contrast
  greenChannelInverted, // pass 2: white-on-red → perfect contrast
  invert,            // pass 3: any white-on-dark
  highContrast,      // pass 4: low-contrast in general
];

function decodeWithZXing(canvas: HTMLCanvasElement): string | null {
  for (const transform of PREPROCESSING_PASSES) {
    try {
      const processed = applyTransform(canvas, transform);
      const luminance = new ZXing.HTMLCanvasElementLuminanceSource(processed);
      const bitmap = new ZXing.BinaryBitmap(new ZXing.HybridBinarizer(luminance));
      // Fresh reader per pass — avoids stale exception state from prior failure
      const result = makeZXingReader().decode(bitmap);
      if (result) return result.getText();
    } catch {
      // NotFoundException thrown for each failed pass — expected, continue
    }
  }
  return null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CameraDevice {
  id: string;
  label: string;
}

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (code: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BarcodeScannerDialog({ open, onOpenChange, onScan }: BarcodeScannerDialogProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scannedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [focusPoint, setFocusPoint] = useState<{ x: number; y: number } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState(false);
  const capturingRef = useRef(false);
  const containerId = 'barcode-scanner-region';

  // Enumerate physical camera devices when the dialog first opens.
  useEffect(() => {
    if (!open) {
      scannedRef.current = false;
      return;
    }
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices.length === 0) return;
        setCameras(devices);
        setSelectedCameraId((prev) => {
          if (prev && devices.some((d) => d.id === prev)) return prev;
          const back = devices.find((d) => /back|rear|traseira/i.test(d.label));
          return back?.id ?? devices[0].id;
        });
      })
      .catch(() => {});
  }, [open]);

  const handleDetect = useCallback((code: string) => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    onScan(code);
    onOpenChange(false);
  }, [onScan, onOpenChange]);

  // Custom scan loop — replaces html5-qrcode's internal loop so we can apply
  // preprocessing passes before ZXing decoding.
  useEffect(() => {
    if (!open || !selectedCameraId) return;

    let mounted = true;

    const stopStream = () => {
      if (rafRef.current !== null) { clearTimeout(rafRef.current); rafRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      videoRef.current = null;
    };

    const start = async () => {
      // iOS releases camera hardware asynchronously after track.stop().
      // getUserMedia called immediately after cleanup returns a live stream
      // object but the video feed is black until the hardware is free.
      // A brief wait is the only reliable fix — the Web API has no "released" event.
      await new Promise<void>((resolve) => setTimeout(resolve, 350));
      if (!mounted) return;

      const container = document.getElementById(containerId);
      if (!container || !mounted) return;

      container.replaceChildren();
      setError(null);

      // Build video element — iOS requires playsinline or it goes fullscreen
      const video = document.createElement('video');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('muted', 'true');
      video.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
      container.appendChild(video);
      videoRef.current = video;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCameraId } },
        });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play();
      } catch (err) {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (/Permission|NotAllowed/i.test(msg)) {
          setError('Permissão da câmera negada. Permita o acesso nas configurações do navegador.');
        } else if (/NotFound|device/i.test(msg)) {
          setError('Nenhuma câmera encontrada neste dispositivo.');
        } else {
          setError(`Erro ao acessar câmera: ${msg}`);
        }
        return;
      }

      // Use native BarcodeDetector when available — it handles preprocessing
      // (contrast, rotation, blur) natively via the OS vision pipeline.
      // Falls back to ZXing + our manual preprocessing passes otherwise.
      const nativeDetector = 'BarcodeDetector' in window
        ? new (window as unknown as { BarcodeDetector: new (opts: object) => { detect(src: HTMLVideoElement | HTMLCanvasElement): Promise<Array<{ rawValue: string }>> } }).BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'codabar', 'qr_code', 'data_matrix'],
          })
        : null;

      const scanCanvas = document.createElement('canvas');

      const doScan = async () => {
        if (!mounted || !videoRef.current || video.readyState < 2) {
          if (mounted) rafRef.current = setTimeout(doScan, 100);
          return;
        }

        let result: string | null = null;

        if (nativeDetector) {
          // BarcodeDetector: works directly on the video element, no canvas needed
          try {
            const hits = await nativeDetector.detect(video);
            if (hits.length > 0) result = hits[0].rawValue;
          } catch { /* camera not ready yet */ }
        } else {
          // ZXing: draw to a small canvas first (faster on high-res streams)
          const base = resizedCanvas(video);
          scanCanvas.width = base.width;
          scanCanvas.height = base.height;
          scanCanvas.getContext('2d')!.drawImage(base, 0, 0);
          result = decodeWithZXing(scanCanvas);
        }

        if (result && mounted) {
          handleDetect(result);
          return;
        }

        if (mounted) {
          // BarcodeDetector runs at 20fps; ZXing at 5fps (preprocessing is CPU-heavy)
          rafRef.current = setTimeout(doScan, nativeDetector ? 50 : 200);
        }
      };

      rafRef.current = setTimeout(doScan, 300); // brief delay for camera to stabilise
    };

    start();

    return () => {
      mounted = false;
      stopStream();
    };
  }, [open, selectedCameraId, handleDetect]);

  // Keep ref in sync so the scan loop can check it synchronously
  useEffect(() => { capturingRef.current = capturing; }, [capturing]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || capturingRef.current) return;

    setCapturing(true);
    setCaptureError(false);

    // Draw the current frame at full camera resolution — no downscale
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);

    // Small delay so the flash animation is visible before the heavy decode
    setTimeout(() => {
      const result = decodeWithZXing(canvas);
      setCapturing(false);

      if (result) {
        handleDetect(result);
      } else {
        setCaptureError(true);
        setTimeout(() => setCaptureError(false), 1500);
      }
    }, 80);
  }, [handleDetect]);

  const handleCameraChange = (deviceId: string) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current !== null) { clearTimeout(rafRef.current); rafRef.current = null; }
    setSelectedCameraId(deviceId);
  };

  const handleTapFocus = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const relX = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const relY = Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1);

    setFocusPoint({ x: clientX - rect.left, y: clientY - rect.top });
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => setFocusPoint(null), 1200);

    const track = (videoRef.current?.srcObject as MediaStream | null)?.getVideoTracks()[0];
    if (!track) return;
    track.applyConstraints({
      advanced: [{ pointsOfInterest: [{ x: relX, y: relY }] } as MediaTrackConstraintSet],
    }).catch(() => {});
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] md:max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5" />
            Escanear Código de Barras
          </DialogTitle>
          <DialogDescription className="text-xs">
            Aponte a câmera para o código de barras do produto
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black">
          <div
            id={containerId}
            className={cn(
              "w-full aspect-square md:aspect-video",
              "[&_video]:w-full [&_video]:object-cover",
              "[&_img]:hidden"
            )}
          />

          {/* Tap-to-focus overlay */}
          {!error && (
            <div
              className="absolute inset-0 cursor-crosshair"
              onClick={handleTapFocus}
              onTouchStart={handleTapFocus}
            >
              {focusPoint && (
                <div
                  className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-sm border-2 border-white animate-in fade-in zoom-in-75 duration-100"
                  style={{ left: focusPoint.x, top: focusPoint.y }}
                />
              )}
            </div>
          )}

          {/* Shutter flash */}
          {capturing && (
            <div className="pointer-events-none absolute inset-0 bg-white/60 animate-in fade-in duration-75" />
          )}

          {/* Capture failed feedback */}
          {captureError && (
            <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
              <span className="rounded-full bg-black/70 px-3 py-1 text-xs text-white">
                Código não encontrado — tente novamente
              </span>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6">
              <div className="text-center space-y-3">
                <X className="h-10 w-10 mx-auto text-destructive" />
                <p className="text-sm text-white">{error}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-3 border-t">
          {cameras.length > 1 ? (
            <Select value={selectedCameraId} onValueChange={handleCameraChange}>
              <SelectTrigger className="flex-1 h-8 text-xs">
                <SelectValue placeholder="Selecionar câmera" />
              </SelectTrigger>
              <SelectContent>
                {cameras.map((cam) => (
                  <SelectItem key={cam.id} value={cam.id} className="text-xs">
                    {cam.label || `Câmera ${cameras.indexOf(cam) + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex-1" />
          )}

          {/* Shutter button — freezes the current frame and runs full-resolution decode */}
          <Button
            variant="default"
            size="sm"
            onClick={handleCapture}
            disabled={capturing || !!error}
            className="gap-1.5"
          >
            <CircleDot className="h-4 w-4" />
            Fotografar
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
